import React, { ReactNode, ElementType } from 'react';
import Button from '@material-ui/core/Button';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import queryString from 'query-string';
import { snakeCase, mapKeys } from 'lodash';

import { darken, fade, makeStyles } from '@material-ui/core/styles';
import {
  FB_BASE,
  LINE_BASE,
  LINE_HOVER,
  LINE_PRESS,
  MAIL_BASE,
  ONE_ODAKYU_BASE,
} from '../../../shared/styles/styleConstants';
import {
  FACEBOOK_AUTHORIZE,
  GOOGLE_AUTHORIZE,
  LINE_AUTHORIZE,
  ONE_AUTHORIZE,
} from '../../constants/routing';
import LineLogo from '../../../shared/images/logo/sns/line.png';
import FBLogo from '../../../shared/images/logo/sns/facebook.png';
import GoogleLogo from '../../../shared/images/logo/sns/google.png';
import ONELogo from '../../../shared/images/logo/sns/one.png';
import { AuthParamKeys } from '~/models/Auth/AuthParamModel';
import { ProviderType } from './constants';

const LOGO_SIZE = 30;

export const makeButtonStyles = (color, base, hover?: any, active?: any) => ({
  backgroundColor: base,
  color,
  ...(!!hover && {
    '&:hover': {
      backgroundColor: hover === true ? fade(base, 0.9) : hover,
    },
  }),
  ...(!!active && {
    '&:active': {
      backgroundColor: active === true ? darken(base, 0.1) : active,
    },
  }),
});

const useStyles = makeStyles((theme) => ({
  header: {
    color: '#333333',
    fontWeight: 'bold',
    fontSize: theme.typography.pxToRem(22),
    marginBottom: theme.spacing(2.5),
    [theme.breakpoints.down('xs')]: {
      fontSize: theme.typography.pxToRem(18),
    },
  },
  description: {
    color: '#333333',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      fontSize: theme.typography.pxToRem(14),
    },
  },
  alert: {
    margin: theme.spacing(0, 'auto', 4),
    maxWidth: 400,
  },
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    '& > *': {
      margin: theme.spacing(0, 'auto', 2.5),
    },
  },
  button: {
    maxWidth: 300,
    width: '100%',
    padding: LOGO_SIZE / 2,
    textTransform: 'none',
    fontWeight: 'bold',
    fontSize: theme.typography.pxToRem(14),
    [theme.breakpoints.up('sm')]: {
      fontSize: theme.typography.pxToRem(16),
    },
    '&:hover': {
      color: theme.palette.common.white,
    },
  },
  logo: {
    width: LOGO_SIZE,
    height: '100%',
  },
  line: makeButtonStyles(
    theme.palette.common.white,
    LINE_BASE,
    LINE_HOVER,
    LINE_PRESS,
  ),
  fb: makeButtonStyles(theme.palette.common.white, FB_BASE, true, true),
  google: {
    ...makeButtonStyles('#444', theme.palette.common.white),
    '&:hover': {
      color: '#444',
    },
  },
  one: makeButtonStyles(
    theme.palette.common.white,
    ONE_ODAKYU_BASE,
    true,
    true,
  ),
  mail: makeButtonStyles(theme.palette.common.white, MAIL_BASE, true, true),
  label: {
    justifyContent: 'flex-start',
  },
  startIcon: {
    marginRight: LOGO_SIZE,
  },
  oneImage: {
    borderRadius: theme.shape.borderRadius,
  },
  note: {
    fontSize: theme.typography.pxToRem(14),
    maxWidth: 300,
    margin: theme.spacing(1, 'auto', 0),
  },
  progress: {
    display: 'flex',
    width: '100%',
    height: '100%',
    minHeight: 300,
    '& > *': {
      margin: 'auto',
    },
  },
}));

interface AuthButtonProps {
  className: string;
  signup?: boolean;
  provider: ReactNode;
  href: string;
  authParams: { [key in AuthParamKeys]: string };
  startIcon: ReactNode;
}

const AuthButton = ({
  className,
  signup,
  provider,
  href,
  authParams,
  ...rest
}: AuthButtonProps) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const authParamStr = queryString.stringify(
    mapKeys(authParams, (v, k) => snakeCase(k)),
  );

  return (
    <Button
      className={clsx(classes.button, className)}
      classes={{ label: classes.label, startIcon: classes.startIcon }}
      variant="contained"
      component="a"
      href={`${process.env.FRONT_HOST}${href}?${authParamStr}`}
      data-method="post"
      {...rest}
    >
      {t(`logins.${signup ? 'signupWith' : 'loginWith'}`, {
        provider,
      })}
    </Button>
  );
};

interface ButtonProps {
  signup?: boolean;
  authParams: { [key in AuthParamKeys]: string };
}

export const LineButton = ({ signup, authParams }: ButtonProps) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const mergedAuthParams = signup
    ? Object.assign(authParams, { consent: 'true' })
    : authParams;
  return (
    <AuthButton
      provider={t('logins.line')}
      signup={signup}
      className={classes.line}
      startIcon={<img src={LineLogo} alt="LINE" className={classes.logo} />}
      href={LINE_AUTHORIZE}
      authParams={mergedAuthParams}
    />
  );
};

export const FacebookButton = ({ signup, authParams }: ButtonProps) => {
  const { t } = useTranslation();
  const classes = useStyles();

  return (
    <AuthButton
      provider={t('logins.facebook')}
      signup={signup}
      className={classes.fb}
      startIcon={<img src={FBLogo} alt="Facebook" className={classes.logo} />}
      href={FACEBOOK_AUTHORIZE}
      authParams={authParams}
    />
  );
};

export const GoogleButton = ({ signup, authParams }: ButtonProps) => {
  const { t } = useTranslation();
  const classes = useStyles();
  return (
    <AuthButton
      provider={t('logins.google')}
      signup={signup}
      className={classes.google}
      startIcon={<img src={GoogleLogo} alt="Google" className={classes.logo} />}
      href={GOOGLE_AUTHORIZE}
      authParams={authParams}
    />
  );
};

export const OneButton = ({ signup, authParams }: ButtonProps) => {
  const { t } = useTranslation();
  const classes = useStyles();
  return (
    <AuthButton
      provider={t('logins.one')}
      signup={signup}
      className={classes.one}
      startIcon={
        <img
          src={ONELogo}
          alt="ONE"
          className={clsx(classes.logo, classes.oneImage)}
        />
      }
      href={ONE_AUTHORIZE}
      authParams={authParams}
    />
  );
};

export const ButtonComponents: {
  [key in ProviderType]: ElementType<ButtonProps>;
} = {
  line: LineButton,
  facebook: FacebookButton,
  google_oauth2: GoogleButton,
  one: OneButton,
};
